import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const DaftarTernakPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ternakList, setTernakList] = useState([]);
  const [filter, setFilter] = useState('all'); // all, sapi, kambing, domba
  const [statusFilter, setStatusFilter] = useState('all'); // all, available, booked, sold
  const [userData, setUserData] = useState({ id: null });
  const [userLoading, setUserLoading] = useState(true);
  const [mitraId, setMitraId] = useState(null);
  const [produks, setProduks] = useState([]);

  // Fetch user data dan mitra_id
  useEffect(() => {
    const fetchUserAndMitra = async () => {
      // Cek apakah ada session/token di localStorage
      const authToken = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      
      // Jika tidak ada token, redirect ke halaman login
      if (!authToken) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        setUserLoading(true);
        // Coba ambil dari user_data di localStorage dulu
        const savedUserData = localStorage.getItem('user_data');
        if (savedUserData) {
          try {
            const parsed = JSON.parse(savedUserData);
            if (parsed.id) {
              // Fetch detail user dari API
              const response = await apiClient.get(API_ENDPOINTS.USER.GET_BY_ID(parsed.id));
              
              let userFromApi = null;
              if (response.data && response.data.user) {
                userFromApi = response.data.user;
              } else if (response.data && response.data.data) {
                userFromApi = response.data.data;
              } else if (response.data && !response.data.user) {
                userFromApi = response.data;
              } else if (!response.data) {
                userFromApi = response;
              }
              
              if (userFromApi) {
                setUserData({
                  id: userFromApi.id || parsed.id
                });
              } else {
                setUserData({ id: parsed.id });
              }
            } else {
              setUserData({ id: parsed.id });
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            // Fallback ke localStorage
            const parsed = JSON.parse(savedUserData);
            setUserData({ id: parsed.id });
          }
        } else {
          // Fetch dari /api/users/me
          const meResponse = await apiClient.get(API_ENDPOINTS.USER.GET_ME);
          if (meResponse.data && meResponse.data.data) {
            const userMe = meResponse.data.data;
            setUserData({ id: userMe.id });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setUserLoading(false);
      }
    };

    fetchUserAndMitra();
  }, [navigate]);

  // Fetch mitra_id dari endpoint /api/mitra/public/user/{user_id}
  useEffect(() => {
    const fetchMitraData = async () => {
      if (userLoading || !userData.id) {
        return;
      }

      if (mitraId) {
        return;
      }

      try {
        console.log('Fetching mitra data untuk user_id:', userData.id);
        const response = await apiClient.get(API_ENDPOINTS.MITRA.PUBLIC_BY_USER(userData.id));
        
        let mitrasFromApi = [];
        if (response.data && response.data.mitras && Array.isArray(response.data.mitras)) {
          mitrasFromApi = response.data.mitras;
        } else if (response.data && Array.isArray(response.data)) {
          mitrasFromApi = response.data;
        } else if (response.mitras && Array.isArray(response.mitras)) {
          mitrasFromApi = response.mitras;
        }
        
        if (mitrasFromApi.length > 0 && mitrasFromApi[0].id) {
          const fetchedMitraId = mitrasFromApi[0].id;
          console.log('Mitra ID ditemukan:', fetchedMitraId);
          setMitraId(fetchedMitraId);
        }
      } catch (error) {
        console.error('Error fetching mitra data:', error);
      }
    };

    fetchMitraData();
  }, [userLoading, userData.id, mitraId]);

  // Fetch produk dari API berdasarkan mitra_id
  useEffect(() => {
    const fetchProduks = async () => {
      if (!mitraId) {
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching produk untuk mitra_id:', mitraId);
        
        // Fetch produk dari API
        const response = await apiClient.get(API_ENDPOINTS.MITRA.PUBLIC_PRODUK(mitraId, 100, 0)); // Limit 100 untuk mendapatkan semua produk
        
        let produksFromApi = [];
        if (response.data && response.data.produks && Array.isArray(response.data.produks)) {
          produksFromApi = response.data.produks;
        } else if (response.produks && Array.isArray(response.produks)) {
          produksFromApi = response.produks;
        }
        
        // Fetch images untuk setiap produk (optional)
        const produksWithImages = await Promise.all(
          produksFromApi.map(async (produk) => {
            try {
              const imagesResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_IMAGES(produk.id));
              let images = [];
              
              if (imagesResponse.data && imagesResponse.data.images) {
                images = imagesResponse.data.images;
              } else if (imagesResponse.images) {
                images = imagesResponse.images;
              } else if (Array.isArray(imagesResponse.data)) {
                images = imagesResponse.data;
              } else if (Array.isArray(imagesResponse)) {
                images = imagesResponse;
              }
              
              return {
                ...produk,
                images: images.length > 0 ? images : [],
                imageUrl: images.length > 0 ? images[0].url || images[0] : null
              };
            } catch (error) {
              return {
                ...produk,
                images: [],
                imageUrl: null
              };
            }
          })
        );
        
        setProduks(produksWithImages);
      } catch (error) {
        console.error('Error fetching produks:', error);
        setProduks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProduks();
  }, [mitraId]);

  // Filter dan transform produk menjadi ternak list
  useEffect(() => {
    if (produks.length === 0) {
      setTernakList([]);
      return;
    }

    // Transform produk menjadi format ternak
    let transformedList = produks.map((produk) => {
      // Tentukan status berdasarkan is_available dan is_sold
      let status = 'Available';
      if (produk.is_sold) {
        status = 'Sold';
      } else if (!produk.is_available) {
        status = 'Booked';
      }
      
      // Tentukan jenis produk
      const jenisProduk = produk.jenis_produk_nama || produk.nama_jenis_produk || 'Produk';
      
      // Format harga
      const harga = produk.harga_jual 
        ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(produk.harga_jual)
        : produk.harga_beli 
        ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(produk.harga_beli)
        : '-';
      
      // Format berat
      const berat = produk.target_bobot ? `${produk.target_bobot} kg` : '-';
      
      // Image URL
      const imageUrl = produk.imageUrl || '/sapi1.jpg';
      
      return {
        id: produk.id,
        nama: produk.nama || 'Produk',
        jenis: jenisProduk,
        berat: berat,
        status: status,
        image: imageUrl,
        harga: harga
      };
    });

    // Filter by jenis
    if (filter !== 'all') {
      transformedList = transformedList.filter(item => {
        const jenisLower = item.jenis.toLowerCase();
        return jenisLower.includes(filter.toLowerCase());
      });
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      transformedList = transformedList.filter(item => item.status.toLowerCase() === statusFilter.toLowerCase());
    }

    setTernakList(transformedList);
  }, [produks, filter, statusFilter]);

  const handleBack = () => {
    navigate('/mitra-area');
  };

  return (
    <section className="min-h-screen bg-white">
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <button 
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
            </button>
            <div className="flex items-center space-x-2">
              <Logo size="small" showText={false} />
              <span className="font-bold text-primary">PAK MoO</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-primary">Daftar Ternak</h1>
        </header>

        {/* Main Content */}
        <main className="pb-24 bg-white">
          {/* Filter Section */}
          <section className="px-4 mt-4 mb-4">
            <div className="space-y-3">
              {/* Jenis Filter */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Jenis Ternak</p>
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      filter === 'all' 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Semua
                  </button>
                  <button
                    onClick={() => setFilter('sapi')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      filter === 'sapi' 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Sapi
                  </button>
                  <button
                    onClick={() => setFilter('kambing')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      filter === 'kambing' 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Kambing
                  </button>
                  <button
                    onClick={() => setFilter('domba')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      filter === 'domba' 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Domba
                  </button>
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Status</p>
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      statusFilter === 'all' 
                        ? 'bg-secondary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Semua
                  </button>
                  <button
                    onClick={() => setStatusFilter('available')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      statusFilter === 'available' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Available
                  </button>
                  <button
                    onClick={() => setStatusFilter('booked')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      statusFilter === 'booked' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Booked
                  </button>
                  <button
                    onClick={() => setStatusFilter('sold')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      statusFilter === 'sold' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Sold
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Ternak List */}
          <section className="px-4 mt-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg p-4 animate-pulse">
                    <div className="flex items-center space-x-4">
                      <div className="w-24 h-24 bg-gray-300 rounded-xl"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : ternakList.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <i className="fa-solid fa-cow text-gray-400 text-5xl mb-4"></i>
                <p className="text-gray-500 text-lg font-medium">Tidak ada ternak ditemukan</p>
                <p className="text-gray-400 text-sm mt-2">Coba ubah filter Anda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ternakList.map((ternak) => (
                  <div 
                    key={ternak.id} 
                    className="bg-white rounded-2xl shadow-lg p-4 cursor-pointer hover:shadow-xl transition-shadow"
                    onClick={() => navigate(`/detail-investasi-sapi/${ternak.id}`)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <img className="w-24 h-24 object-cover rounded-xl" src={ternak.image} alt={ternak.nama} />
                        {ternak.status === 'Available' && (
                          <span className="absolute top-2 right-2 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                            Available
                          </span>
                        )}
                        {ternak.status === 'Booked' && (
                          <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                            Booked
                          </span>
                        )}
                        {ternak.status === 'Sold' && (
                          <span className="absolute top-2 right-2 bg-gray-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                            Sold
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-800">{ternak.nama}</h4>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            ternak.jenis === 'Sapi' ? 'bg-orange-100 text-orange-600' :
                            ternak.jenis === 'Kambing' ? 'bg-green-100 text-green-600' :
                            'bg-blue-100 text-blue-600'
                          }`}>
                            {ternak.jenis}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">Berat: {ternak.berat}</p>
                        <p className="text-sm font-semibold text-primary mt-1">{ternak.harga}</p>
                        <p className="text-xs text-gray-500 mt-1">Status: {ternak.status}</p>
                      </div>
                      <i className="fa-solid fa-chevron-right text-gray-400"></i>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>
    </section>
  );
};

export default DaftarTernakPage;

